/**
 * @leadership/roles — shared role constants and permission helpers.
 * Consumed by both the web app and the mobile app so permission rules
 * can't drift between platforms.
 *
 * Source: role constants originally in apps/web/src/shared/lib/roles.ts,
 * mobile helpers from apps/mobile/src/utils/roles.ts.
 */

// ─── Ward (bishopric) account roles ───────────────────────────────────────

export const ACCOUNT_ROLES = [
  'bishop',
  'counselor1',
  'counselor2',
  'clerk',
  'exec_secretary',
  // Ward Mission Leader — deliberately NOT in WARD_EDIT_ROLES. Their only
  // authority is over their own ward's missionary-work content (the meal
  // host-ward assignment on the /missionary calendar); they get no edit
  // rights over meetings, callings, interviews or anything else on the ward.
  // See ROLE_GROUP's 'missionary' group and canEditMealSlot below.
  'ward_mission_leader',
  'test',
] as const

export type AccountRole = (typeof ACCOUNT_ROLES)[number]

export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  bishop: 'Bishop',
  counselor1: '1st Counselor',
  counselor2: '2nd Counselor',
  clerk: 'Clerk',
  exec_secretary: 'Executive Secretary',
  ward_mission_leader: 'Ward Mission Leader',
  superadmin: 'Super Admin',
  test: 'Test User',
}

// ─── Stake account roles ──────────────────────────────────────────────────

export const STAKE_ACCOUNT_ROLES = [
  'stake_president',
  'stake_counselor1',
  'stake_counselor2',
  'stake_exec_secretary',
  'stake_clerk',
  'high_council',
  'stake_leader',
] as const

export type StakeAccountRole = (typeof STAKE_ACCOUNT_ROLES)[number]

export const STAKE_ROLE_DISPLAY_NAMES: Record<string, string> = {
  stake_president: 'Stake President',
  stake_counselor1: '1st Counsellor',
  stake_counselor2: '2nd Counsellor',
  stake_exec_secretary: 'Executive Secretary',
  stake_clerk: 'Clerk',
  high_council: 'High Council',
  stake_leader: 'Stake Leader',
  superadmin: 'Super Admin',
}

// Coarse role groups used for per-meeting visibility checks. Unified across
// stake and ward — the group names ("presidency", "clerk") are domain
// concepts that apply to both. A bishop maps to "presidency" because
// bishopric ≈ stake presidency for ward-meeting visibility purposes.
export const ROLE_GROUP: Record<string, string> = {
  // Stake roles
  stake_president: 'presidency',
  stake_counselor1: 'presidency',
  stake_counselor2: 'presidency',
  stake_clerk: 'clerk',
  stake_exec_secretary: 'clerk',
  high_council: 'highCouncil',
  stake_leader: 'stakeLeader',
  // Ward roles
  bishop: 'presidency',
  counselor1: 'presidency',
  counselor2: 'presidency',
  clerk: 'clerk',
  exec_secretary: 'clerk',
  // Its OWN group, not 'presidency'. Putting a ward mission leader in
  // 'presidency' would hand them visibility of every bishopric-restricted
  // meeting in the ward — their remit is missionary work only.
  ward_mission_leader: 'missionary',
  // Both
  superadmin: 'superadmin',
}

/** Backwards-compat alias. New code should import ROLE_GROUP directly. */
export const STAKE_ROLE_GROUP = ROLE_GROUP

/**
 * Sort priority for leader-option dropdowns on stake-side calling
 * assignments. Lower number → listed first.
 *
 *   0  Stake presidency, clerks, secretaries (the people most likely
 *      to be assigned to track a calling — surface them first)
 *   1  High council (next most common assignees)
 *   2  Everyone else (organization leaders, stake leaders, members,
 *      anyone with a non-stake role we happen to have an entry for)
 *
 * Used by the AssignedSelect dropdown in StakeCallingWorkflowsView
 * (web) and StakeCallingDetailScreen (mobile). Web's stakeLeaderOptions
 * builder also stamps the priority per-source since several of its
 * inputs (stakeData.stakePresidency, clerksSecretaries, etc.) don't
 * carry a STAKE_ACCOUNT_ROLES role string we can read directly.
 */
export function getStakeAssignmentSortPriority(role: string | null | undefined): number {
  if (!role) return 2
  const group = ROLE_GROUP[role]
  if (group === 'presidency' || group === 'clerk') return 0
  if (group === 'highCouncil') return 1
  return 2
}

export const getUserRoleGroup = (role: string | null | undefined): string | null =>
  (role && ROLE_GROUP[role]) || null

// The visibility groups that per-meeting permission checks render.
// Stake-only set (legacy alias). New code should call getRoleGroupsFor(orgType).
export const ROLE_GROUPS = [
  { key: 'presidency', label: 'Stake Presidency' },
  { key: 'highCouncil', label: 'High Council' },
  { key: 'stakeLeader', label: 'Stake Leader' },
  { key: 'clerk', label: 'Clerk / Secretary' },
]

// Ward-flavored visibility groups. Bishop / counselors map to the
// 'presidency' group via ROLE_GROUP, so picking "Bishopric" on a ward
// meeting limits visibility to bishop + counselors. High Council and
// Stake Leader don't exist on ward — excluded.
export const WARD_ROLE_GROUPS = [
  { key: 'presidency', label: 'Bishopric' },
  { key: 'clerk', label: 'Clerk / Secretary' },
]

/** Returns the org-type-appropriate visibility group list. */
export function getRoleGroupsFor(orgType: 'stake' | 'ward' = 'stake') {
  return orgType === 'ward' ? WARD_ROLE_GROUPS : ROLE_GROUPS
}

// Roles that can add / edit / delete stake content. Everyone else is view-only.
export const STAKE_EDIT_ROLES = [
  'stake_president',
  'stake_counselor1',
  'stake_counselor2',
  'stake_clerk',
  'stake_exec_secretary',
  'superadmin',
] as const

export const canEditStake = (role: string | null | undefined): boolean =>
  STAKE_EDIT_ROLES.includes(role as never)

/** A stake High Council member (account role 'high_council'). */
export const isHighCouncil = (role: string | null | undefined): boolean =>
  role === 'high_council'

/**
 * Whether a user may advance/complete a calling or ordination workflow.
 * Stake edit-roles (presidency / clerk / secretary / superadmin) always can.
 * A High Council member may advance a workflow ONLY when it is assigned to
 * them (`isAssigned`). For HC this is enforced server-side by the
 * calling-workflow route — HC can't write the `stakeCallings` blob directly —
 * so this helper only drives the UI affordances; it is not the security gate.
 */
export const canAdvanceAssignedWorkflow = (
  role: string | null | undefined,
  isAssigned: boolean,
): boolean => canEditStake(role) || (isHighCouncil(role) && isAssigned)

/**
 * Who sees the "assigned to you" calling/ordination dashboard alert: High
 * Council (the intended audience — they don't live in the app) plus superadmin
 * (the all-access owner/test account, which sees everything else too). Normal
 * stake roles (presidency / clerk) are intentionally excluded — they have the
 * "Your assignments" card.
 */
export const seesAssignedWorkflowAlert = (role: string | null | undefined): boolean =>
  isHighCouncil(role) || role === 'superadmin'

// Roles that can edit the Speaking Assignments page. Matches the
// general `STAKE_EDIT_ROLES` set — same edit gate as everywhere else
// in the stake app. Kept as a separate constant so the gate can be
// narrowed again later without churning callsites.
export const STAKE_SPEAKING_EDIT_ROLES = [
  'stake_president',
  'stake_counselor1',
  'stake_counselor2',
  'stake_clerk',
  'stake_exec_secretary',
  'superadmin',
] as const

export const canEditSpeakingAssignments = (role: string | null | undefined): boolean =>
  STAKE_SPEAKING_EDIT_ROLES.includes(role as never)

// Roles that can add / edit / delete ward content (bishopric + clerks).
export const WARD_EDIT_ROLES = [
  'bishop',
  'counselor1',
  'counselor2',
  'clerk',
  'exec_secretary',
  'superadmin',
] as const

export const canEditWard = (role: string | null | undefined): boolean =>
  WARD_EDIT_ROLES.includes(role as never)

/**
 * Unified edit-permission check for the meetings module — works for both
 * stake and ward orgs. Used by code paths that don't know (or shouldn't
 * care) which org type they're rendering for.
 */
export const canEditMeetings = (
  role: string | null | undefined,
  orgType: 'stake' | 'ward',
): boolean => (orgType === 'stake' ? canEditStake(role) : canEditWard(role))

// ─── Role display-label tables ────────────────────────────────────────────

export const STAKE_PRESIDENCY_ROLE_LABELS: Record<string, string> = {
  president: 'Stake President',
  counselor1: '1st Counsellor',
  counselor2: '2nd Counsellor',
}

export const CLERK_ROLE_LABELS: Record<string, string> = {
  stake_clerk: 'Stake Clerk',
  asst_stake_clerk: 'Assistant Stake Clerk',
  exec_secretary: 'Executive Secretary',
  asst_exec_secretary: 'Assistant Executive Secretary',
}

// ─── Mobile-origin permission helpers ─────────────────────────────────────

export function getRoleDisplayName(role: string): string {
  return ROLE_DISPLAY_NAMES[role] || role
}

export function isPresidency(role: string): boolean {
  return ['bishop', 'counselor1', 'counselor2'].includes(role)
}

export function canManageUsers(role: string): boolean {
  return ['bishop', 'clerk', 'superadmin'].includes(role)
}

/**
 * Ward roles allowed to see an interview flagged `private`. The flag means
 * "bishop + executive secretary only" — counselors and clerks are excluded,
 * including when a counselor is the listed interviewer. Superadmins are in
 * for support. Keep this in sync with PRIVATE_INTERVIEW_AUDIENCE_WARD, the
 * copy shown on the add/edit form.
 */
export const WARD_PRIVATE_INTERVIEW_ROLES = ['bishop', 'exec_secretary', 'superadmin']

/** Form copy naming who the ward `private` flag actually limits an interview to. */
export const PRIVATE_INTERVIEW_AUDIENCE_WARD = 'the bishop and executive secretary'

/** Stake counterpart of the ward copy above (see StakeInterviewsTab's gate). */
export const PRIVATE_INTERVIEW_AUDIENCE_STAKE = 'the stake president and executive secretary'

export function canSeePrivateInterviews(role: string): boolean {
  return WARD_PRIVATE_INTERVIEW_ROLES.includes(role)
}

// ─── Configurable permission model (stake RBAC) ────────────────────────────
// Permission keys, default grants, and the hasPermission helper used by the
// settings panel and every client/server gate. See ./permissions.ts.
export * from './permissions'
